import React, { useEffect, useRef, useState } from "react";
import { Plus, FileText, Upload, Send, X, CheckCircle2, Clock, Building2, Image as ImageIcon, Loader2, CreditCard, Printer, Download, Eye, Award, Gift, BadgePercent } from "lucide-react";
import api from "../lib/api";
import { printAppDoc, downloadAppDoc, displayStatus, isPending, sectionColor } from "../lib/appDoc";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-800",
  Draft: "bg-slate-100 text-slate-600", Submitted: "bg-blue-100 text-blue-700",
  "Under Review": "bg-amber-100 text-amber-700", Reviewed: "bg-indigo-100 text-indigo-700",
  "On Hold": "bg-yellow-100 text-yellow-800", Offer: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700", Accepted: "bg-emerald-100 text-emerald-700",
  Declined: "bg-orange-100 text-orange-700", Withdrawn: "bg-slate-100 text-slate-500",
};
const REVIEW_COLORS = {
  Approved: "bg-green-100 text-green-700", Rejected: "bg-red-100 text-red-700",
  Reviewed: "bg-blue-100 text-blue-700", "Offer Received": "bg-teal-100 text-teal-700",
};
const INTAKES = ["Jan 2026", "May 2026", "Sep 2026", "Jan 2027"];
const LEVELS = ["Foundation", "Diploma", "Undergraduate", "Postgraduate", "PhD"];

export default function StudentApplications() {
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [unis, setUnis] = useState([]);
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ university_id: "", programme: "", intake: "", level: "", dob: "", nationality: "", prev_education: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [responding, setResponding] = useState("");
  const [fee, setFee] = useState(null);
  const [uniOffers, setUniOffers] = useState(null);
  const [paying, setPaying] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/universities").then((r) => setUnis(r.data)).catch(() => {});
    api.get("/portal/student/profile").then((r) => setProfile(r.data.profile || {})).catch(() => {});
    // Handle Stripe payment return
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success" && params.get("session_id")) {
      const sid = params.get("session_id");
      let tries = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") { toast({ title: "Payment successful! 🎉", description: "Your application fee has been received." }); load(); return; }
        } catch (_) {}
        if (tries++ < 6) setTimeout(poll, 1500);
        else toast({ title: "Payment is processing", description: "We'll update the status shortly." });
      };
      poll();
      window.history.replaceState({}, "", window.location.pathname + "?tab=applications");
    } else if (params.get("payment") === "cancel") {
      toast({ title: "Payment cancelled", variant: "destructive" });
      window.history.replaceState({}, "", window.location.pathname + "?tab=applications");
    }
  }, []);

  const selectedUni = unis.find((u) => u.id === form.university_id);

  const studentName = profile?.full_name || profile?.name || "";
  const docFromDetail = (d) => ({
    docLabel: "University Application", heading: d.university_name, student: studentName, status: d.status,
    rows: [
      ["Programme", d.programme], ["Intake", d.intake], ["Level", d.level],
      ["Nationality", d.nationality], ["Previous Education", d.prev_education],
      ["Submitted", d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : ""],
    ],
    documents: (d.documents || []).map((f) => f.name),
  });
  const docFromCard = (a) => ({
    docLabel: "University Application", heading: a.university_name, student: studentName, status: a.status,
    rows: [["Programme", a.programme], ["Intake", a.intake], ["Documents", `${a.documents_count || 0} uploaded`]],
  });
  const offerDocOpts = (d) => ({
    docLabel: "Letter of Offer", heading: d.university_name, student: studentName, status: d.status,
    ref: d.student_code || d.id,
    sections: [
      { title: "Programme", rows: [
        ["Programme", d.programme], ["Level", d.level], ["Intake", d.intake],
        ["Campus", d.campus], ["Field of study", d.field_of_study],
      ]},
      { title: "Personal", rows: [
        ["Candidate", studentName], ["Nationality", d.nationality], ["Date of birth", d.dob],
      ]},
      { title: "Funding", rows: [
        ["Offer status", d.status],
        ["Offer letter", d.offer_letter ? d.offer_letter.name : ""],
        ["Next step", "Accept your offer and settle the application fee in your portal to confirm your seat."],
      ]},
    ],
    documents: d.offer_letter ? [`${d.offer_letter.name} (official signed offer)`] : [],
  });
  const receiptDocOpts = (d, f) => ({
    docLabel: "Payment Receipt", heading: "Application Fee Receipt", student: studentName, status: "Paid",
    ref: `RCPT-${(d.id || "").slice(0, 8).toUpperCase()}`,
    sections: [
      { title: "Funding", rows: [
        ["Receipt no.", `RCPT-${(d.id || "").slice(0, 8).toUpperCase()}`],
        ["Amount paid", `${(f?.currency || "").toUpperCase()} ${f?.amount ?? ""}`],
        ["Payment status", "Paid"],
        ["Paid on", d.fee_paid_at ? new Date(d.fee_paid_at).toLocaleString() : new Date().toLocaleString()],
        ["Method", "Card (Stripe)"],
      ]},
      { title: "Programme", rows: [
        ["University", d.university_name], ["Programme", d.programme], ["Intake", d.intake], ["Level", d.level],
      ]},
      { title: "Personal", rows: [
        ["Paid by", studentName], ["Student code", d.student_code || ""],
      ]},
    ],
  });
  const profileDocOpts = () => ({
    docLabel: "Profile Application", heading: "Your profile application", student: studentName,
    status: profile?.status,
    rows: [
      ["Programme of choice", profile?.programme_of_choice || profile?.interest],
      ["Preferred country", profile?.country],
      ["Email", profile?.email], ["Mobile", profile?.mobile],
      ["Counsellor review", profile?.review_status || "Pending"],
      ["Submitted", profile?.submitted_at ? new Date(profile.submitted_at).toLocaleDateString() : ""],
    ],
  });

  const fmtAddr = (a) => a && typeof a === "object" ? [a.line1, a.line2, a.city, a.state, a.country, a.zip].filter(Boolean).join(", ") : (a || "");
  const uniName = (() => { const u = unis.find((x) => x.id === profile?.institute_of_choice); return u ? `${u.name} — ${u.country}` : profile?.institute_of_choice; })();
  const fullProfileSections = () => {
    const p = profile || {};
    return [
      { title: "Personal", rows: [
        ["Full name", p.full_name || studentName], ["Gender", p.gender], ["Date of birth", p.dob],
        ["Nationality", p.nationality], ["ID", p.id_category ? `${p.id_category}: ${p.id_number || ""}` : ""],
        ["Email", p.email], ["Mobile", p.mobile ? `${p.dial_code || ""} ${p.mobile}` : ""], ["Tel", p.tel],
      ]},
      { title: "Address", rows: [
        ["Current address", fmtAddr(p.current_address) || [p.address, p.city, p.country].filter(Boolean).join(", ")],
        ["Permanent address", p.same_as_current ? "Same as current" : fmtAddr(p.permanent_address)],
      ]},
      { title: "Programme", rows: [
        ["Institute of choice", uniName], ["Campus", p.campus],
        ["Mode / Type", [p.programme_mode, p.programme_type].filter(Boolean).join(" · ")],
        ["Programme applied", p.programme_of_choice], ["Field of study", p.field_of_study],
        ["Preferred intake", p.preferred_intake], ["Remarks", p.remarks],
      ]},
      { title: "English proficiency", rows: [
        ["Test", p.english_test], ["Overall", p.english_overall], ["Test date", p.english_test_date], ["Scores", p.english_scores],
      ]},
      { title: "Passport & visa", rows: [
        ["Passport no.", p.passport_no], ["Issued", p.passport_issue], ["Expiry", p.passport_expiry],
        ["Place of issue", p.passport_place], ["Guardian passport", p.guardian_passport_no],
        ["Visa refusal", p.visa_refusal === "Yes" ? `Yes — ${p.visa_refusal_details || ""}` : p.visa_refusal],
      ]},
      { title: "Guardian / emergency", rows: [
        ["Name", p.guardian_name], ["Relation", p.guardian_relation], ["Phone", p.guardian_phone],
        ["Email", p.guardian_email], ["Address", fmtAddr(p.guardian_address)],
      ]},
      { title: "Emergency contact", rows: [
        ["Name", p.emergency_contact_name], ["Relation", p.emergency_contact_relation],
        ["Phone", p.emergency_contact_phone], ["Email", p.emergency_contact_email],
      ]},
      { title: "Funding", rows: [
        ["Source", p.funding_source], ["Sponsor", p.sponsor_name], ["Budget", p.budget],
      ]},
      { title: "Statement of purpose", rows: [["SOP", p.statement_of_purpose]] },
    ];
  };
  const fullProfileDoc = () => ({
    docLabel: "Student Application", heading: "Student Application — Full Details", student: studentName,
    status: profile?.status,
    rows: [["Counsellor review", profile?.review_status || "Pending"], ["Submitted", profile?.submitted_at ? new Date(profile.submitted_at).toLocaleDateString() : "Not yet submitted"]],
    sections: fullProfileSections(),
    documents: (profile?.documents || []).map((d) => d.name || d.label).filter(Boolean),
  });


  const create = async () => {
    if (!form.university_id) { toast({ title: "Please select a university", variant: "destructive" }); return; }
    setSaving(true);
    try { await api.post("/applications", form); toast({ title: "Application created (Draft)" }); setOpen(false); setForm({ university_id: "", programme: "", intake: "", level: "", dob: "", nationality: "", prev_education: "", notes: "" }); load(); }
    catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const openApp = async (id) => {
    setActive(id); setFee(null); setUniOffers(null);
    const { data } = await api.get(`/applications/${id}`); setDetail(data);
    api.get(`/payments/application-fee/${id}`).then((r) => setFee(r.data)).catch(() => {});
    if (data.university_id) api.get(`/universities/${data.university_id}`).then((r) => setUniOffers(r.data)).catch(() => {});
  };
  const refresh = async () => {
    if (active) {
      const { data } = await api.get(`/applications/${active}`); setDetail(data); load();
      api.get(`/payments/application-fee/${active}`).then((r) => setFee(r.data)).catch(() => {});
    }
  };

  const payFee = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/application-fee/checkout", { application_id: active, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast({ title: "Couldn't start payment", description: e?.response?.data?.detail || "", variant: "destructive" });
      setPaying(false);
    }
  };

  const submit = async () => { await api.put(`/applications/${active}`, { status: "Submitted" }); toast({ title: "Application submitted!" }); refresh(); };


  const respondOffer = async (decision) => {
    setResponding(decision);
    try {
      await api.put(`/applications/${active}`, { status: decision });
      toast({ title: decision === "Accepted" ? "Offer accepted! 🎉" : "Offer declined", description: decision === "Accepted" ? "Your counsellor will guide you on next steps." : "You can discuss other options with your counsellor." });
      refresh();
    } catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setResponding(""); }
  };

  const onFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const kind = file.type.startsWith("image") ? "image" : "file";
      await api.post(`/applications/${active}/documents`, { name: file.name, data: reader.result, kind });
      refresh(); toast({ title: "Document uploaded" });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-[#012a1e]">My Applications</h2>
        <Button onClick={() => setOpen(true)} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-5"><Plus size={18} className="mr-1" /> New Application</Button>
      </div>

      {apps.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="application-status-summary">
          <span className="text-sm text-slate-500 mr-1">{apps.length} application{apps.length > 1 ? "s" : ""}:</span>
          {Object.entries(apps.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {})).map(([st, n]) => (
            <span key={st} data-testid={`status-chip-${st}`} className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[st] || "bg-slate-100 text-slate-600"}`}>{st} · {n}</span>
          ))}
        </div>
      )}

      {profile && (profile.status === "Submitted" || profile.review_status) && (
        <div className="mt-4 bg-white rounded-2xl border border-slate-100 p-4 flex flex-wrap items-center gap-3" data-testid="profile-status-banner">
          <div className="w-10 h-10 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030]"><CheckCircle2 size={20} /></div>
          <div className="flex-1 min-w-[160px]">
            <p className="text-sm font-semibold text-[#012a1e]">Your profile application</p>
            <p className="text-xs text-slate-500">{profile.programme_of_choice || "Study abroad"}{profile.submitted_at ? ` · submitted ${new Date(profile.submitted_at).toLocaleDateString()}` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            {profile.status === "Submitted" && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-blue-100 text-blue-700">Submitted</span>}
            {profile.status !== "Submitted" && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-800">Pending</span>}
            {profile.review_status && <span data-testid="profile-review-status" className={`text-xs font-semibold px-3 py-1 rounded-full ${REVIEW_COLORS[profile.review_status] || "bg-slate-100 text-slate-600"}`}>Counsellor: {profile.review_status}</span>}
          </div>
          <div className="flex items-center gap-1 w-full sm:w-auto border-t sm:border-t-0 border-slate-100 pt-2 sm:pt-0">
            <button data-testid="profile-app-view" onClick={() => setShowFull(true)} title="View full application" className="flex items-center gap-1 text-xs font-semibold text-white bg-[#004030] hover:bg-[#013d2b] px-3 py-1.5 rounded-full"><Eye size={14} /> View</button>
            <button data-testid="profile-app-print" onClick={() => printAppDoc(fullProfileDoc())} title="Print" className="flex items-center gap-1 text-xs font-semibold text-[#004030] px-2 py-1 rounded-lg hover:bg-[#f0f6f3]"><Printer size={14} /> Print</button>
            <button data-testid="profile-app-download" onClick={() => downloadAppDoc(fullProfileDoc())} title="Download PDF" className="flex items-center gap-1 text-xs font-semibold text-[#004030] px-2 py-1 rounded-lg hover:bg-[#f0f6f3]"><Download size={14} /> PDF</button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        {apps.map((a) => (
          <div key={a.id} data-testid={`application-card-${a.id}`} className="bg-white rounded-2xl border border-slate-100 p-5 hover-lift">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-[#f0f6f3] grid place-items-center text-[#004030]"><Building2 size={20} /></div>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[displayStatus(a.status)] || STATUS_COLORS[a.status]}`}>{displayStatus(a.status)}</span>
            </div>
            <p className="font-display text-lg font-bold text-[#012a1e] mt-3">{a.university_name}</p>
            <p className="text-slate-500 text-sm">{a.programme || "Programme TBD"} · {a.intake || "Intake TBD"}</p>
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><FileText size={12} /> {a.documents_count} document(s)</p>
            <div className="flex items-center gap-1 mt-4 pt-3 border-t border-slate-100">
              <button data-testid={`app-view-${a.id}`} onClick={() => openApp(a.id)} className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-white bg-[#004030] hover:bg-[#013d2b] rounded-full py-2"><Eye size={14} /> View</button>
              <button data-testid={`app-print-${a.id}`} onClick={() => printAppDoc(docFromCard(a))} title="Print" className="flex items-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-2"><Printer size={14} /></button>
              <button data-testid={`app-download-${a.id}`} onClick={() => downloadAppDoc(docFromCard(a))} title="Download PDF" className="flex items-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-2"><Download size={14} /></button>
            </div>
          </div>
        ))}
        {!apps.length && (
          <div className="sm:col-span-2 bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
            <p className="text-slate-400">No applications yet. Start your first university application.</p>
            <Button onClick={() => setOpen(true)} className="mt-3 bg-[#004030] text-white rounded-full px-6"><Plus size={16} className="mr-1" /> New Application</Button>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New University Application</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>University *</Label>
              <Select value={form.university_id} onValueChange={(v) => setForm({ ...form, university_id: v, programme: "" })}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Choose a university" /></SelectTrigger>
                <SelectContent>{unis.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.country}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedUni && (
              <div className="space-y-2">
                <Label>Programme</Label>
                <Select value={form.programme} onValueChange={(v) => setForm({ ...form, programme: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Choose a programme" /></SelectTrigger>
                  <SelectContent>{(selectedUni.programmes || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Intake</Label>
                <Select value={form.intake} onValueChange={(v) => setForm({ ...form, intake: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{INTAKES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Level</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="h-11" /></div>
              <div className="space-y-2"><Label>Nationality</Label><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} className="h-11" /></div>
            </div>
            <div className="space-y-2"><Label>Previous Education</Label><Textarea value={form.prev_education} onChange={(e) => setForm({ ...form, prev_education: e.target.value })} placeholder="e.g. High School, GPA, board..." className="min-h-20" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} className="bg-[#004030] hover:bg-[#013d2b] text-white">Create Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet */}
      <Sheet open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDetail(null); } }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-display text-xl">{detail.university_name}</SheetTitle></SheetHeader>
              <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[displayStatus(detail.status)] || STATUS_COLORS[detail.status]}`}>{displayStatus(detail.status)}</span>
                <div className="flex items-center gap-1">
                  <button data-testid="detail-print" onClick={() => printAppDoc(docFromDetail(detail))} className="flex items-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-1.5"><Printer size={14} /> Print</button>
                  <button data-testid="detail-download" onClick={() => downloadAppDoc(docFromDetail(detail))} className="flex items-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-1.5"><Download size={14} /> PDF</button>
                </div>
              </div>
              <div className="mt-4 space-y-1 text-sm text-slate-600">
                <p><b>Programme:</b> {detail.programme || "—"}</p>
                <p><b>Intake:</b> {detail.intake || "—"} · <b>Level:</b> {detail.level || "—"}</p>
                <p><b>Nationality:</b> {detail.nationality || "—"}</p>
              </div>

              {/* Status timeline */}
              <div className="mt-5 flex items-center gap-1 flex-wrap">
                {["Draft", "Submitted", "Under Review", "Offer", "Accepted"].map((s, i) => {
                  const order = ["Draft", "Submitted", "Under Review", "Offer", "Accepted"];
                  const done = order.indexOf(detail.status) >= i || detail.status === "Accepted";
                  return (
                    <div key={s} className="flex items-center">
                      <div className={`flex items-center gap-1 text-xs ${done ? "text-[#004030] font-semibold" : "text-slate-400"}`}>
                        {done ? <CheckCircle2 size={14} /> : <Clock size={14} />} {s}
                      </div>
                      {i < 4 && <span className="w-4 h-px bg-slate-200 mx-1" />}
                    </div>
                  );
                })}
              </div>

              {/* Documents */}
              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#012a1e] text-sm">Documents</h3>
                  <button onClick={() => fileRef.current?.click()} className="text-xs text-[#004030] font-semibold flex items-center gap-1"><Upload size={13} /> Upload</button>
                  <input ref={fileRef} type="file" hidden onChange={onFile} />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {(detail.documents || []).map((f) => (
                    <a key={f.id} href={f.data} target="_blank" rel="noopener noreferrer" className="border border-slate-200 rounded-lg p-2 text-center hover:border-[#004030]">
                      {f.kind === "image" ? <img src={f.data} alt={f.name} className="h-14 w-full object-cover rounded" /> : <FileText size={26} className="mx-auto text-[#004030]" />}
                      <p className="text-[10px] truncate mt-1">{f.name}</p>
                    </a>
                  ))}
                  {!(detail.documents || []).length && <p className="text-xs text-slate-400 col-span-3">Upload passport, transcripts, SOP, etc.</p>}
                </div>
              </div>

              {detail.status === "Draft" && (
                <Button onClick={submit} className="w-full mt-6 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-12 font-semibold">Submit Application <Send size={16} className="ml-1" /></Button>
              )}

              {detail.offer_letter && (detail.status === "Offer" || detail.status === "Accepted") && (
                <div className="mt-5 space-y-2">
                  <a href={detail.offer_letter.data} download={detail.offer_letter.name} target="_blank" rel="noreferrer" data-testid="download-offer-letter" className="flex items-center gap-2 rounded-xl border border-[#004030]/20 bg-[#f0f6f3] p-3 hover:border-[#004030]">
                    <FileText size={20} className="text-[#004030]" />
                    <div className="flex-1"><p className="text-sm font-semibold text-[#012a1e]">Official Offer Letter</p><p className="text-xs text-slate-500">{detail.offer_letter.name}</p></div>
                    <Upload size={16} className="text-[#004030] rotate-180" />
                  </a>
                  <div className="flex items-center gap-2">
                    <button data-testid="offer-print" onClick={() => printAppDoc(offerDocOpts(detail))} className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-2"><Printer size={14} /> Print offer summary</button>
                    <button data-testid="offer-download" onClick={() => downloadAppDoc(offerDocOpts(detail))} className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-2"><Download size={14} /> PDF</button>
                  </div>
                </div>
              )}

              {detail.status === "Offer" && (
                <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-4" data-testid="offer-panel">
                  <p className="text-sm font-semibold text-[#012a1e] flex items-center gap-1"><CheckCircle2 size={16} className="text-green-600" /> You've received an offer! 🎉</p>
                  <p className="text-xs text-slate-500 mt-1">Respond to {detail.university_name}'s offer for {detail.programme || "your programme"}.</p>
                  <div className="flex gap-2 mt-3">
                    <Button onClick={() => respondOffer("Accepted")} disabled={!!responding} data-testid="accept-offer-btn" className="flex-1 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-11 font-semibold">
                      {responding === "Accepted" ? <Loader2 className="animate-spin" size={16} /> : "Accept Offer"}
                    </Button>
                    <Button onClick={() => respondOffer("Declined")} disabled={!!responding} variant="outline" data-testid="decline-offer-btn" className="flex-1 rounded-full h-11 font-semibold border-slate-300">
                      {responding === "Declined" ? <Loader2 className="animate-spin" size={16} /> : "Decline"}
                    </Button>
                  </div>
                </div>
              )}

              {uniOffers && (uniOffers.application_fee_waiver || uniOffers.early_bird_offer || (uniOffers.scholarships || []).length > 0) && (
                <div className="mt-4 rounded-2xl border border-[#e0c05a]/50 bg-[#fffdf5] p-4" data-testid="uni-offers-panel">
                  <p className="font-display font-bold text-[#012a1e] text-sm flex items-center gap-1.5"><Gift size={16} className="text-[#c9a227]" /> Offers &amp; scholarships at {uniOffers.name}</p>
                  <div className="mt-3 space-y-2">
                    {uniOffers.application_fee_waiver && (
                      <div className="flex items-start gap-2 text-sm" data-testid="offer-fee-waiver">
                        <BadgePercent size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                        <span className="text-slate-700"><b className="text-emerald-700">Application fee waiver</b>{uniOffers.application_fee_waiver_note ? ` — ${uniOffers.application_fee_waiver_note}` : ""}</span>
                      </div>
                    )}
                    {uniOffers.early_bird_offer && (
                      <div className="flex items-start gap-2 text-sm" data-testid="offer-early-bird">
                        <Clock size={16} className="text-amber-600 shrink-0 mt-0.5" />
                        <span className="text-slate-700"><b className="text-amber-700">Early bird:</b> {uniOffers.early_bird_offer}{uniOffers.early_bird_deadline ? ` (by ${uniOffers.early_bird_deadline})` : ""}</span>
                      </div>
                    )}
                    {(uniOffers.scholarships || []).map((s, i) => (
                      <div key={s.id || i} className="flex items-start gap-2 text-sm" data-testid={`offer-scholarship-${i}`}>
                        <Award size={16} className="text-[#004030] shrink-0 mt-0.5" />
                        <span className="text-slate-700">
                          <b className="text-[#004030]">{s.name}</b>
                          {s.coverage ? ` — ${s.coverage}` : ""}
                          {s.intake ? ` · ${s.intake}` : ""}
                          {s.deadline ? ` · apply by ${s.deadline}` : ""}
                          {s.eligibility ? <span className="block text-xs text-slate-500">{s.eligibility}</span> : null}
                          {s.link ? <a href={s.link} target="_blank" rel="noreferrer" className="text-xs text-[#004030] underline ml-1">Details</a> : null}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">Offers are set by the university and may change. Confirm with your counsellor before paying.</p>
                </div>
              )}

              {detail.status === "Accepted" && (
                <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4" data-testid="offer-response-state">
                  <p className="text-sm font-semibold text-emerald-700">You accepted this offer. 🎓</p>
                  {fee?.fee_status === "Paid" ? (
                    <div className="mt-2" data-testid="fee-paid-state">
                      <p className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 size={15} /> Application fee paid{fee.amount ? ` (${(fee.currency || "").toUpperCase()} ${fee.amount})` : ""}.</p>
                      <button data-testid="download-receipt-btn" onClick={() => downloadAppDoc(receiptDocOpts(detail, fee))} className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-[#004030] border border-emerald-200 bg-white hover:bg-[#f0f6f3] rounded-full px-3 py-2.5"><Download size={14} /> Download payment receipt</button>
                    </div>
                  ) : fee?.waived ? (
                    <div className="mt-2" data-testid="fee-waived-state">
                      <p className="text-sm text-emerald-700 flex items-center gap-1.5"><BadgePercent size={16} /> <span><b>Application fee waived</b>{fee.base_amount ? ` — you save ${(fee.currency || "").toUpperCase()} ${fee.base_amount}` : ""}.</span></p>
                      {fee.waiver_note && <p className="text-xs text-slate-500 mt-1">{fee.waiver_note}</p>}
                      <p className="text-xs text-slate-500 mt-1">No payment is required to proceed with this application.</p>
                    </div>
                  ) : fee?.fee_found ? (
                    <div className="mt-3">
                      <p className="text-sm text-slate-600 mb-2">Pay your application fee of <b>{(fee.currency || "").toUpperCase()} {fee.amount}</b> to proceed.</p>
                      <Button onClick={payFee} disabled={paying} data-testid="pay-fee-btn" className="w-full bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-11 font-bold">
                        {paying ? <Loader2 className="animate-spin" size={16} /> : <><CreditCard size={16} className="mr-1" /> Pay Application Fee</>}
                      </Button>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-slate-500">Your counsellor will confirm the application fee shortly.</p>
                  )}
                </div>
              )}

              {detail.status === "Declined" && (
                <div className="mt-4 rounded-2xl p-4 text-sm font-semibold bg-orange-50 text-orange-700 border border-orange-200" data-testid="offer-response-state">
                  You declined this offer.
                </div>
              )}

              {/* Notes */}
              {(detail.notes || []).length > 0 && (
                <div className="mt-5">
                  <h3 className="font-semibold text-[#012a1e] text-sm">Updates from GDIH</h3>
                  <div className="space-y-2 mt-2">
                    {detail.notes.map((n) => (
                      <div key={n.id} className="bg-[#f7faf8] rounded-lg p-3"><p className="text-sm text-slate-700">{n.text}</p><p className="text-[10px] text-slate-400 mt-1">{n.by_name} · {new Date(n.at).toLocaleString()}</p></div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Full student application view */}
      <Dialog open={showFull} onOpenChange={setShowFull}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="full-application-view">
          <DialogHeader><DialogTitle className="font-display text-xl text-[#012a1e]">Student Application</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[displayStatus(profile?.status)] || "bg-amber-100 text-amber-800"}`}>{displayStatus(profile?.status)}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => printAppDoc(fullProfileDoc())} data-testid="full-app-print" className="flex items-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-1.5"><Printer size={14} /> Print</button>
              <button onClick={() => downloadAppDoc(fullProfileDoc())} data-testid="full-app-download" className="flex items-center gap-1 text-xs font-semibold text-[#004030] border border-slate-200 hover:bg-[#f0f6f3] rounded-full px-3 py-1.5"><Download size={14} /> PDF</button>
            </div>
          </div>
          {profile?.status !== "Submitted" && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1">This application is still <b>pending</b> — not yet submitted. You can continue editing it in <b>My Profile</b>.</p>}
          <div className="mt-2 space-y-4">
            {fullProfileSections().map((sec) => {
              const rows = sec.rows.filter(([, v]) => v);
              if (!rows.length) return null;
              const c = sectionColor(sec.title);
              return (
                <div key={sec.title} className="bg-white rounded-lg border border-slate-200/80 shadow-sm overflow-hidden" style={{ borderLeft: `4px solid ${c.hex}` }} data-testid={`full-section-${sec.title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="px-4 py-2.5 font-display font-semibold text-sm" style={{ background: c.bg, color: c.hex }}>{sec.title}</div>
                  <div className="divide-y divide-slate-50 px-4">
                    {rows.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 py-2 text-sm"><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 pt-0.5">{k}</span><span className="font-semibold text-[#1a231f] text-right max-w-[62%]">{v}</span></div>
                    ))}
                  </div>
                </div>
              );
            })}
            {(profile?.education_history || []).length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200/80 shadow-sm overflow-hidden" style={{ borderLeft: "4px solid #134e4a" }}>
                <div className="px-4 py-2.5 font-display font-semibold text-sm" style={{ background: "#f0fdfa", color: "#134e4a" }}>Education history</div>
                <div className="p-3 space-y-2">
                {(profile.education_history || []).map((e, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-2.5 bg-[#f9fcfb] text-sm">
                    <p className="font-semibold text-[#1a231f]">{[e.course, e.level].filter(Boolean).join(" · ") || "Qualification"}</p>
                    <p className="text-xs text-slate-500">{[e.stream, e.institution === "Other" ? e.institution_other : e.institution, e.country, e.year_of_passing].filter(Boolean).join(" · ")}</p>
                  </div>
                ))}
                </div>
              </div>
            )}
            {(profile?.documents || []).length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200/80 shadow-sm overflow-hidden" style={{ borderLeft: "4px solid #4c1d95" }}>
                <div className="px-4 py-2.5 font-display font-semibold text-sm" style={{ background: "#f5f3ff", color: "#4c1d95" }}>Supporting documents</div>
                <div className="grid grid-cols-3 gap-2 p-3">
                  {profile.documents.map((f, i) => (
                    <a key={i} href={f.data} target="_blank" rel="noreferrer" className="border border-slate-200 rounded-lg p-2 text-center hover:border-[#4c1d95] transition-colors" data-testid={`full-app-doc-${i}`}>
                      {f.kind === "image" ? <img src={f.data} alt={f.name} className="h-14 w-full object-cover rounded" /> : <FileText size={24} className="mx-auto text-[#4c1d95]" />}
                      <p className="text-[10px] truncate mt-1">{f.label || f.name}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
